import levenshtein from 'fast-levenshtein';
import { deburr } from 'lodash-es';
import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import Button from 'react-bootstrap/Button';
import Dropdown from 'react-bootstrap/Dropdown';
import Form from 'react-bootstrap/Form';
import { DISABLED, NO_LIMIT } from '#src/constants/limits';
import { useI18n } from '#src/hooks/i18n';
import {
  type AggregationGroup,
  type Compare,
  type SelectionMode,
} from '#src/types';
import Icon from '../ui/icon';
import { OptionTag } from '../ui/option-tag';

type Option = {
  id: string;
  name: string;
};

function getDefaultSelectionMode(
  comparisonLimit: number,
  selection: Compare,
  mode?: SelectionMode
): SelectionMode {
  if (
    selection.length > 0 ||
    comparisonLimit === DISABLED ||
    mode === 'custom'
  ) {
    return 'custom';
  }
  return 'default';
}

export function CompareForm({
  formId,
  onChange,
  shortName,
  options,
  defaultSelectionLabel,
  selection,
  label,
  close,
  mode,
  showDefaultSelection = true,
  comparisonLimit = 5,
  aggregationLimit = 0,
}: {
  formId: string;
  shortName: string;
  label: string;
  selection: Compare;
  showDefaultSelection?: boolean;
  defaultSelectionLabel: string | undefined;
  options: Option[];
  onChange: (state: {
    selectionMode: SelectionMode;
    selection: Compare;
  }) => void;
  close?: () => void;
  comparisonLimit?: number;
  aggregationLimit?: number;
  mode?: SelectionMode;
}) {
  const { t } = useI18n();

  const defaultSelectionMode = getDefaultSelectionMode(
    comparisonLimit,
    selection,
    mode
  );
  const [state, setState] = useState({
    selectionMode: defaultSelectionMode,
    selection: selection ?? [[]],
  });

  const availableOptions = options.filter((op) => {
    return !state.selection.some((group) => group.includes(op.id));
  });

  const contentRef = useRef<HTMLDivElement>(null);
  const [shouldScroll, setShouldScroll] = useState(false);
  const radioRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const [focusTarget, setFocusTarget] = useState<string | null>(null);
  const prevSelectionLength = useRef(state.selection.length);

  // Restore focus after state change
  useEffect(() => {
    if (focusTarget && radioRefs.current[focusTarget]) {
      radioRefs.current[focusTarget]?.focus();
      setFocusTarget(null); // Clear after focusing
    }
  }, [focusTarget, state.selectionMode]);

  // Focus newly added dropdown
  useEffect(() => {
    if (state.selection.length > prevSelectionLength.current) {
      // Find the last aggregation group dropdown
      const lastIndex = state.selection.length - 1;
      const lastDropdown = contentRef.current?.querySelector(
        `.aggregation-group-dropdown-toggle[data-index="${lastIndex}"]`
      ) as HTMLDivElement;

      if (lastDropdown) {
        lastDropdown.focus();
      }
    }
    prevSelectionLength.current = state.selection.length;
  }, [state.selection.length]);

  // Check if content overflows and needs scrolling
  useLayoutEffect(() => {
    const checkOverflow = () => {
      if (!contentRef.current) return;

      const content = contentRef.current;

      // Calculate available height: 75vh - button area (57px) - padding (1.5rem = 24px)
      const footerHeight = 57;
      const padding = 24;
      const maxAvailableHeight =
        window.innerHeight * 0.75 - footerHeight - padding;
      const contentHeight = content.scrollHeight;

      setShouldScroll(contentHeight > maxAvailableHeight);
    };

    checkOverflow();

    // Use ResizeObserver to detect content changes
    const resizeObserver = new ResizeObserver(checkOverflow);
    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    // Also listen to window resize
    window.addEventListener('resize', checkOverflow);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkOverflow);
    };
  }, [state.selection.length, state.selectionMode]);

  const handleChange = (
    selectionMode: SelectionMode,
    selection: Compare,
    focusKey?: string
  ) => {
    setState({
      selectionMode,
      selection: selection,
    });
    if (focusKey) {
      setFocusTarget(focusKey);
    }
  };

  const handleClear = () => {
    const newState = {
      selectionMode: mode ?? ('default' as SelectionMode),
      selection: [[]],
    };
    handleChange(newState.selectionMode, newState.selection);
    onChange(newState);
    if (close) close();
  };
  const handleCancel = () => {
    setState({ selectionMode: defaultSelectionMode, selection });
    if (close) close();
  };
  const handleApply = () => {
    const sanitizedState = {
      ...state,
      selection: state.selection.filter((s) => s.length > 0),
    };
    onChange(sanitizedState);
    if (close) close();
  };
  const onAddComparison = () => {
    setState((prevState) => ({
      ...prevState,
      selection: [...prevState.selection, []],
    }));
  };

  return (
    <div className='compare-form px-3 bg-white rounded-bottom w-100'>
      <div
        ref={contentRef}
        className='d-flex flex-column max-w-100'
        style={{
          maxHeight: shouldScroll ? 'calc(60vh - 57px - 1.5rem)' : 'auto',
          overflowY: shouldScroll ? 'auto' : 'visible',
        }}
      >
        <div
          role='radiogroup'
          aria-label={`${shortName} selection mode`}
          className='d-flex flex-column w-100 mb-2'
        >
          {showDefaultSelection && (
            <Form.Check
              type='radio'
              id={`${formId}-default`}
              name={`${formId}-selectionMode`}
              label={defaultSelectionLabel}
              value='default'
              checked={state.selectionMode === 'default'}
              onChange={(ev) =>
                handleChange(
                  ev.target.value as SelectionMode,
                  [[]],
                  `${formId}-default`
                )
              }
              ref={(el: HTMLInputElement | null) => {
                radioRefs.current[`${formId}-default`] = el;
              }}
              className='mb-2 text-dark small'
            />
          )}
          {showDefaultSelection && (
            <Form.Check
              type='radio'
              id={`${formId}-custom`}
              name={`${formId}-selectionMode`}
              label={label}
              value='custom'
              checked={state.selectionMode === 'custom'}
              onChange={(ev) =>
                handleChange(
                  ev.target.value as SelectionMode,
                  [[]],
                  `${formId}-custom`
                )
              }
              ref={(el: HTMLInputElement | null) => {
                radioRefs.current[`${formId}-custom`] = el;
              }}
              className='mb-2 text-dark small'
            />
          )}
        </div>
        <div className='d-flex flex-column w-100 mb-2'>
          <div className='d-flex flex-row align-items-center mb-3'>
            <AggregationGroupDropdown
              index={0}
              group={state.selection[0] ?? []}
              options={availableOptions}
              allOptions={options}
              aggregationLimit={aggregationLimit}
              disabled={state.selectionMode !== 'custom'}
              onChange={(group) => {
                handleChange(state.selectionMode, [
                  group,
                  ...state.selection.slice(1),
                ]);
              }}
              label={`${t('filters.common.select')} ${shortName}`}
              placeholder={t('filters.compare.select-option-placeholder')}
            />
          </div>
          {state.selection.slice(1).map((group, index) => (
            <div
              key={index + 1}
              className='d-flex flex-row align-items-center mb-3'
            >
              <AggregationGroupDropdown
                group={group}
                index={index + 1}
                options={availableOptions}
                allOptions={options}
                aggregationLimit={aggregationLimit}
                disabled={state.selectionMode !== 'custom'}
                onChange={(group) => {
                  const groupIndex = index + 1;
                  const newCompare = state.selection.map((s, i) =>
                    i === groupIndex ? group : s
                  );
                  handleChange(state.selectionMode, newCompare);
                }}
                label={t('filters.compare.compare-against')}
                placeholder={t('filters.compare.select-option-placeholder')}
              />
            </div>
          ))}
        </div>
        <div className='d-flex w-100 mb-2'>
          <Button
            type='button'
            disabled={
              state.selectionMode !== 'custom' ||
              state.selection.length === comparisonLimit ||
              availableOptions.length === 0 ||
              comparisonLimit === DISABLED
            }
            variant='light'
            className='add-comparison-btn border-secondary btn-xs text-nowrap py-2 d-flex align-items-center'
            onClick={onAddComparison}
          >
            {t('filters.compare.add-comparison')}
            <Icon name='plus' className='ml-2' />
          </Button>
        </div>
      </div>
      <div
        className='compare-form-actions d-flex pt-3 w-100 flex-shrink-0'
        style={{ height: '57px' }}
      >
        <Button
          type='button'
          variant='link'
          className='text-secondary flex-grow-1 btn-xs mr-2 text-nowrap py-2 px-0 font-weight-normal'
          onClick={handleClear}
        >
          {t('filters.common.clear')}
        </Button>
        <Button
          type='button'
          variant='light'
          className='border-secondary flex-grow-1 btn-xs mr-2 text-nowrap py-2'
          onClick={handleCancel}
        >
          {t('filters.common.cancel')}
        </Button>
        <Button
          type='button'
          variant='secondary'
          className='border-secondary flex-grow-1 btn-xs text-nowrap py-2'
          onClick={handleApply}
        >
          {t('filters.common.apply')}
        </Button>
      </div>
    </div>
  );
}

function AggregationGroupDropdown({
  group,
  index,
  options,
  allOptions,
  disabled,
  onChange,
  label,
  placeholder,
  aggregationLimit,
}: {
  group: AggregationGroup;
  index: number;
  options: Option[];
  allOptions: Option[];
  disabled: boolean;
  onChange: (group: AggregationGroup) => void;
  label: string;
  placeholder: string;
  aggregationLimit: number;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<any>(null);
  const dropdownToggleRef = useRef<HTMLDivElement>(null);
  const prevGroupLength = useRef(group.length);

  // Force dropdown position recalculation when group changes
  useEffect(() => {
    if (isOpen && group.length !== prevGroupLength.current) {
      // Force a re-render of the dropdown menu by temporarily toggling visibility
      setIsOpen(false);
      setTimeout(() => setIsOpen(true), 0);

      prevGroupLength.current = group.length;
    }
    prevGroupLength.current = group.length;
  }, [group.length, isOpen]);

  const handleDropdownToggle = (nextShow: boolean, event: any) => {
    if (
      event &&
      event.target &&
      dropdownToggleRef.current &&
      dropdownToggleRef.current.contains(event.target)
    ) {
      // Always allow toggling when clicking the toggle
      setIsOpen(nextShow);
      return;
    }
    if (!nextShow) {
      if (
        event &&
        event.target &&
        dropdownRef.current &&
        dropdownRef.current.contains(event.target)
      ) {
        // Click inside dropdown menu, ignore close
        return;
      }
    }
    setIsOpen(nextShow);
  };

  const optionLabels = allOptions.reduce(
    (acc, opt) => {
      acc[opt.id] = opt.name;
      return acc;
    },
    {} as Record<string, string>
  );
  const OptionsMenu =
    aggregationLimit === DISABLED ? SingleSelectMenu : MultipleSelectMenu;

  return (
    <div className='d-flex flex-column flex-grow-1'>
      <span
        style={{
          fontWeight: 500,
        }}
        className='small mb-1 text-dark'
      >
        {label}
      </span>
      <Dropdown
        ref={dropdownRef}
        drop='down'
        className='flex-grow-1'
        show={isOpen}
        onToggle={handleDropdownToggle}
      >
        <Dropdown.Toggle
          as='div'
          ref={dropdownToggleRef}
          variant='light'
          disabled={disabled}
          className='aggregation-group-dropdown-toggle dropdown-toggle-border'
          data-index={index}
          role='button'
          tabIndex={disabled ? -1 : 0}
          aria-label={label}
          aria-expanded={isOpen}
          aria-disabled={disabled}
          aria-controls={`dropdown-menu-${index}`}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (disabled) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              if (e.repeat) return; // Prevent repeat firing when holding key
              setIsOpen(!isOpen);
            } else if (e.key === 'Escape' && isOpen) {
              e.preventDefault();
              setIsOpen(false);
            }
          }}
        >
          {group.length === 0 && (
            <span className='font-weight-normal small text-muted'>
              {placeholder}
            </span>
          )}
          {group.length > 0 && (
            <div className='d-flex flex-row flex-wrap align-items-center'>
              {group.map((item) => (
                <OptionTag
                  key={item}
                  label={optionLabels[item]}
                  onClick={() => {
                    onChange(group.filter((i) => i !== item));
                    // Focus the dropdown toggle after removal
                    setTimeout(() => dropdownToggleRef.current?.focus(), 0);
                  }}
                />
              ))}
            </div>
          )}
        </Dropdown.Toggle>
        <OptionsMenu
          options={options}
          group={group}
          id={index}
          onChange={(group) => {
            onChange(group);
            setIsOpen(false);
          }}
          aggregationLimit={aggregationLimit}
        />
      </Dropdown>
    </div>
  );
}

function MultipleSelectMenu({
  options,
  group,
  id,
  onChange,
  aggregationLimit,
}: {
  options: Option[];
  group: AggregationGroup;
  id: string | number;
  aggregationLimit: number;
  onChange: (group: AggregationGroup) => void;
}) {
  const [search, setSearch] = useState('');
  const { t } = useI18n();

  const filteredOptions = options.filter((opt) => {
    if (search.length === 0) return true;
    const sanitizedSearch = deburr(search.toLowerCase());
    const sanitizedName = deburr(opt.name.toLowerCase());
    const distance = levenshtein.get(sanitizedName, sanitizedSearch);

    return sanitizedName.includes(sanitizedSearch) || distance <= 3;
  });

  return (
    <Dropdown.Menu
      id={`dropdown-menu-${id}`}
      className='w-100 p-3'
      style={{ minWidth: 200, maxHeight: 300, overflowY: 'auto' }}
    >
      <div className='d-flex flex-column flex-grow-1'>
        <div className='search-input-container'>
          <Form.Control
            type='search'
            placeholder={t('filters.compare.search-placeholder')}
            className='border-secondary'
            onChange={(e) => setSearch(e.target.value)}
            aria-label={t('filters.compare.search-placeholder')}
          />
          <Icon
            name='search'
            className='search-input-icon'
            aria-hidden='true'
          />
        </div>

        {filteredOptions.map((opt) => (
          <Form.Check
            key={opt.id}
            id={`${id}-${opt.id}`}
            type='checkbox'
            label={opt.name}
            checked={group.includes(opt.id)}
            disabled={
              aggregationLimit > NO_LIMIT && group.length === aggregationLimit
            }
            onChange={() => {
              const values = group.includes(opt.id)
                ? group.filter((i) => i !== opt.id)
                : [...group, opt.id];
              onChange(values);
            }}
          />
        ))}
      </div>
    </Dropdown.Menu>
  );
}

function SingleSelectMenu({
  options,
  id,
  onChange,
}: {
  options: Option[];
  group: AggregationGroup;
  id: string | number;
  onChange: (group: AggregationGroup) => void;
}) {
  const { t } = useI18n();
  const [search, setSearch] = useState('');

  const filteredOptions = options.filter((opt) => {
    if (search.length === 0) return true;
    const sanitizedSearch = deburr(search.toLowerCase());
    const sanitizedName = deburr(opt.name.toLowerCase());
    const distance = levenshtein.get(sanitizedName, sanitizedSearch);

    return sanitizedName.includes(sanitizedSearch) || distance <= 3;
  });

  return (
    <Dropdown.Menu
      id={`dropdown-menu-${id}`}
      className='w-100 p-3'
      style={{ minWidth: 200, maxHeight: 300, overflowY: 'auto' }}
    >
      <div className='d-flex flex-column flex-grow-1'>
        <div className='search-input-container'>
          <Form.Control
            type='search'
            placeholder={t('filters.compare.search-placeholder')}
            className='border-secondary'
            onChange={(e) => setSearch(e.target.value)}
            aria-label={t('filters.compare.search-placeholder')}
          />
          <Icon
            name='search'
            className='search-input-icon'
            aria-hidden='true'
          />
        </div>
        <div className='d-flex flex-column flex-grow-1'>
          {filteredOptions.map((opt) => (
            <Dropdown.Item
              as='button'
              key={opt.id}
              eventKey={opt.id}
              className='small'
              onClick={() => {
                onChange([opt.id]);
              }}
            >
              {opt.name}
            </Dropdown.Item>
          ))}
        </div>
      </div>
    </Dropdown.Menu>
  );
}
